/**
 * The graph: history to the left, now in the middle, futures to the right.
 *
 * Reading direction does the work. Time runs left to right so "further right" means
 * "later", which is the one spatial metaphor nobody has to be taught. The rail scrolls
 * horizontally, and every chord that lands scrolls it to its right-hand end, so that the
 * options are whole and the history runs off behind them.
 *
 * Everything here is derived from `SessionState`. The rail has no memory of its own, which
 * is what lets `/dev/states` render any moment of any session without a piano.
 */

import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { LayoutGroup } from 'framer-motion';
import type { BranchSuggestion, CanonicalChord, SessionNode, SessionState } from '@hc/shared';
import type { SessionPhase } from '../system/sessionPhase.js';
import { hypothesisToChord, suggestionToChord } from '../system/chordReading.js';
import { BranchColumn, type BranchExpansion } from './BranchColumn.js';
import { CurrentNode } from './CurrentNode.js';
import { HistoryNode } from './HistoryNode.js';
import { ListeningPulse } from './ListeningPulse.js';
import { PathConnector } from './PathConnector.js';
import type { GhostStub } from './GhostStubs.js';

/** A move nobody suggested gets the neutral tint — never a different weight. */
const NEUTRAL_ADVENTUROUSNESS = 0.5;

export interface GraphRailProps {
  state: SessionState;
  phase: SessionPhase;
  calm: boolean;
  onOpenCorrection: () => void;
  onRetryBranches: () => void;
  /**
   * On the Go: a branch card is a thing you can take by hand.
   *
   * It is what draws "Take this" in an open card, and nothing else — a tap on a card
   * opens it at every width. See `BranchCard`.
   */
  onSelectBranch?: (suggestion: BranchSuggestion) => void;
  /** The two stages: the first tap on a branch card opens it rather than acting. */
  branchExpansion?: BranchExpansion;
  /**
   * Tapping a history node opens its detail.
   *
   * It used to rewind on the spot, which discarded the forward path with no preview from a
   * target the size of a chord symbol. The rewind still exists and is unchanged — it is
   * the primary action *inside* the detail. See `HistoryDetailSheet`.
   */
  onOpenHistory?: (nodeId: string) => void;
  /** Reopens the chord picker. Sits with the branches because it is the sixth option. */
  onOpenPicker?: () => void;
  /** Chrome anchored to the current node. Passed straight through; see `CurrentNode`. */
  currentNodeChrome?: ReactNode;
}

/**
 * The options offered at `node` that the player did not take.
 *
 * Often empty — the reducer prunes branch sets more than one step back, and that is the
 * right trade: the ghosts are atmosphere, and keeping every set alive to draw four dots
 * would be a memory leak with a decorative justification.
 */
function ghostsFor(
  state: SessionState,
  node: SessionNode,
  next: SessionNode | undefined,
): GhostStub[] {
  const set = state.branchSets[node.id];
  if (!set) return [];
  return set.suggestions
    .filter((suggestion) => suggestion.id !== next?.fromSuggestionId)
    .map((suggestion) => ({
      id: suggestion.id,
      adventurousness: suggestion.adventurousness,
      label: suggestion.displaySymbol,
    }));
}

/**
 * On the Go, before anything has been chosen.
 *
 * Where the piano gets a breathing dot proving the room is being heard, this gets a
 * sentence and a button — there is nothing to prove, only something to do. A pulse here
 * would be a microphone animation on a device whose microphone was never opened, which is
 * the one thing the mode promises not to imply.
 */
function FirstChordPrompt({ onOpenPicker }: { onOpenPicker?: () => void }): React.JSX.Element {
  return (
    <li className="hc-rail__item hc-rail__item--current">
      <div className="hc-first-chord" data-testid="hc-choose-first">
        <p className="hc-first-chord__lead">Choose your first chord</p>
        {onOpenPicker ? (
          <button
            type="button"
            className="hc-button hc-button--primary"
            data-testid="hc-open-picker"
            onClick={onOpenPicker}
          >
            Pick a chord
          </button>
        ) : null}
      </div>
    </li>
  );
}

/** How adventurous the move *into* this node was, for the connector tint. */
function incomingAdventurousness(state: SessionState, node: SessionNode, index: number): number {
  if (!node.fromSuggestionId || index === 0) return NEUTRAL_ADVENTUROUSNESS;
  const previous = state.path[index - 1];
  if (!previous) return NEUTRAL_ADVENTUROUSNESS;
  const set = state.branchSets[previous.id];
  const taken = set?.suggestions.find((s) => s.id === node.fromSuggestionId);
  return taken?.adventurousness ?? NEUTRAL_ADVENTUROUSNESS;
}

export function GraphRail({
  state,
  phase,
  calm,
  onOpenCorrection,
  onRetryBranches,
  onSelectBranch,
  branchExpansion,
  onOpenHistory,
  onOpenPicker,
  currentNodeChrome,
}: GraphRailProps): React.JSX.Element {
  const railRef = useRef<HTMLDivElement | null>(null);
  const currentRef = useRef<HTMLLIElement | null>(null);

  const currentIndex = state.path.findIndex((node) => node.id === state.currentNodeId);
  const history = currentIndex >= 0 ? state.path.slice(0, currentIndex) : state.path;

  // Respelling every suggestion is cheap but not free, and it happens on every render of
  // a screen that re-renders whenever the recogniser breathes. Memoised on the two things
  // it actually depends on.
  const chordCache = useMemo(() => {
    const cache = new Map<string, CanonicalChord>();
    return (suggestion: BranchSuggestion): CanonicalChord => {
      const existing = cache.get(suggestion.id);
      if (existing) return existing;
      const chord = suggestionToChord(suggestion, state.keyEstimate);
      cache.set(suggestion.id, chord);
      return chord;
    };
  }, [state.keyEstimate]);

  const alternative = useMemo(() => {
    const runnerUp = phase.detected?.alternatives[0];
    if (!runnerUp) return null;
    return hypothesisToChord(runnerUp, state.keyEstimate);
  }, [phase.detected, state.keyEstimate]);

  /*
   * A chord lands; the rail goes to its right-hand end.
   *
   * The owner asked for this twice without knowing it was one request — "it still doesn't
   * quite fit on the page", with a photograph of branch cards cut off mid-word, and "for
   * new chords please auto scroll to the right when selected". A 517-point viewport with
   * one chord on it measured `scrollWidth: 586, clientWidth: 517, scrollLeft: 0`: the
   * branch column hung 69 points past the right edge and nothing ever moved the rail.
   *
   * This used to park the current node about a third of the way in, and that anchor was
   * not wrong so much as *outvoted*. The branch column is the last thing in the track, so
   * "the whole set is on screen" and "the rail is scrolled to its end" are the same
   * sentence — and the anchor already resolved to the end everywhere the two disagreed by
   * enough to matter, because a target past the maximum is clamped to it. On an iPad and
   * on a desktop the number this computes is the number the anchor computed. The one place
   * they differed is the start of a narrow session, where the current node is near the
   * left edge, the anchor asks for a negative scroll, and the cards are cut in half. So
   * the anchor is gone rather than special-cased: one rule, no width in it.
   *
   * (The end is exactly where the cards end. `.hc-rail__track`'s trailing padding is not
   * counted in an overflowing flex container's `scrollWidth` — a real and long-standing
   * quirk — which is why the gutter the cards need is `padding-right` on the branch item
   * itself. See `graph.css`.)
   *
   * Keyed on the current node alone: suggestions streaming in, a card opening, a window
   * resizing must not yank a rail somebody is reading. Not smooth under reduced motion,
   * and never awaited by anything — the recogniser does not know this ran.
   */
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !currentRef.current) return;
    const end = Math.max(0, rail.scrollWidth - rail.clientWidth);
    if (typeof rail.scrollTo === 'function') {
      rail.scrollTo({ left: end, behavior: calm ? 'auto' : 'smooth' });
    } else {
      rail.scrollLeft = end;
    }
  }, [state.currentNodeId, calm]);

  const branches = (
    <BranchColumn
      phase={phase.branches}
      branchSet={phase.branchSet}
      previousBranchSet={phase.previousBranchSet}
      chordFor={chordCache}
      calm={calm}
      onRetry={onRetryBranches}
      {...(onSelectBranch ? { onSelect: onSelectBranch } : {})}
      {...(branchExpansion ? { expansion: branchExpansion } : {})}
    />
  );

  return (
    <div className="hc-rail" data-testid="hc-graph-rail" ref={railRef}>
      <LayoutGroup id="hc-graph">
        {/*
          With no chord yet there is no graph to align to, so whatever stands in for it —
          the breathing dot at the piano, the invitation On the Go — centres rather than
          sitting at the left edge where the first history node would go.
        */}
        <ol className={`hc-rail__track${phase.current ? '' : ' hc-rail__track--empty'}`}>
          {history.map((node, index) => (
            <HistoryNode
              key={node.id}
              node={node}
              symbol={node.chord.symbol}
              ghosts={ghostsFor(state, node, state.path[index + 1])}
              distance={history.length - index}
              calm={calm}
              incomingAdventurousness={incomingAdventurousness(state, node, index)}
              showConnector={index > 0}
              {...(onOpenHistory ? { onOpen: onOpenHistory } : {})}
            />
          ))}

          {/*
            The connector into "now". This is the state-9-versus-state-10 line: it is
            drawn by the same component with the same weight whether the player took a
            suggestion or went their own way. Only the tint differs, and only because
            an unsuggested move has no adventurousness to report.
          */}
          {phase.current ? (
            <li className="hc-rail__joint" ref={currentRef} aria-hidden="true">
              {history.length > 0 ? (
                <PathConnector
                  adventurousness={incomingAdventurousness(state, phase.current, currentIndex)}
                  testId="hc-connector-current"
                />
              ) : null}
            </li>
          ) : null}

          {phase.current ? (
            <CurrentNode
              node={phase.current}
              chord={phase.current.chord}
              alternative={alternative}
              detected={phase.detected}
              reading={phase.reading}
              keyEstimate={state.keyEstimate}
              calm={calm}
              arrivedBySuggestion={phase.arrivedBySuggestion}
              onOpenCorrection={onOpenCorrection}
              {...(currentNodeChrome === undefined ? {} : { chrome: currentNodeChrome })}
            />
          ) : phase.chooseFirstChord ? (
            <FirstChordPrompt onOpenPicker={onOpenPicker} />
          ) : (
            <ListeningPulse calm={calm} />
          )}

          {phase.current ? (
            <li className="hc-rail__item hc-rail__item--branches">
              {/*
                The wrapper exists only where the picker does. Without it the column is the
                direct flex child it has always been, so the piano layout — which is tuned
                to the millimetre against six cards on the shortest iPad — is untouched.
              */}
              {onOpenPicker ? (
                <div className="hc-branches-stack">
                  {branches}
                  {/*
                    The escape from the offered set, kept beside it rather than in a menu.
                    "An unsuggested chord is never an error" only holds On the Go if going
                    somewhere nobody suggested is as easy as taking a card.
                  */}
                  <button
                    type="button"
                    className="hc-any-chord"
                    data-testid="hc-any-chord"
                    onClick={onOpenPicker}
                  >
                    + any chord
                  </button>
                </div>
              ) : (
                branches
              )}
            </li>
          ) : null}
        </ol>
      </LayoutGroup>
    </div>
  );
}
